import { TripService } from '@app/api/trip.service';
import { Component, ElementRef, OnDestroy, ViewChild } from '@angular/core';
import { Location } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { combineLatest, Observable, of, Subject } from 'rxjs';
import { debounceTime, map, takeUntil, tap } from 'rxjs/operators';
import { ExperienceEndpoint } from '@shared/models/be-experience.model';
import { FEExperience } from '@shared/models/fe-experience.model';
import { ExperienceDetailsService } from '@domain/experience-details.service';
import { WindowEventsService } from '@app/core/services/window-events/window-events.service';
import { HeaderBG } from '@shared/models/header-background.enum';
import { ExperienceStyle } from '@ess-front/shared';
import { HistoryService } from 'src/app/core/services/history/history.service';
import { Trip } from '@shared/models/itinerary/trip.model';

@Component({
  selector: 'ess-experience',
  templateUrl: './experience.component.html',
  styleUrls: ['./experience.component.scss'],
  standalone: false,
})
export class ExperienceComponent implements OnDestroy {
  endpoint: ExperienceEndpoint;
  isBooking: boolean;
  ExperienceStyle = ExperienceStyle;

  routeData$: Observable<{
    experience: FEExperience | null;
    trip: Trip | null;
  }> | null = null;

  hash: string | null = null;
  id: number | null = null;
  slug: string | null = null;

  maxScroll = 0;
  scrollRatio$: Observable<number>;
  resize$: Observable<Event>;
  destroy$ = new Subject<void>();
  isMobileResolution$: Observable<boolean>;

  scroll$: Observable<Event>;
  titleVisible$: Observable<boolean> | null = null;
  headerBG$: Observable<HeaderBG>;

  @ViewChild('gallerySection') gallerySection: ElementRef | null = null;
  @ViewChild('editorTipsSection') editorTipsSection: ElementRef | null = null;

  constructor(
    private activatedRoute: ActivatedRoute,
    private experienceDetailsService: ExperienceDetailsService,
    private location: Location,
    private titleService: Title,
    private windowEventsService: WindowEventsService,
    private historyService: HistoryService,
    private router: Router,
    private tripService: TripService,
  ) {
    this.endpoint = this.activatedRoute.snapshot.data['endpoint'];
    this.isBooking = this.endpoint === ExperienceEndpoint.BookingExperience;
    if (this.isBooking) {
      this.hash = this.activatedRoute.snapshot.params['hash'];
      this.id = parseInt(this.activatedRoute.snapshot.params['id']);
    } else {
      this.slug = this.activatedRoute.snapshot.params['slug'];
    }

    this.routeData$ = combineLatest([
      this.hash ? this.tripService.getTrip$(this.hash) : of(null),
      this.experienceDetailsService
        .getFEExperience$(this.isBooking, this.endpoint, {
          slug: this.slug,
          id: this.id,
          hash: this.hash,
        })
        .pipe(tap(experience => this.titleService.setTitle(experience.title))),
    ]).pipe(
      map(([trip, experience]) => ({
        trip: trip,
        experience: experience,
      })),
    );

    this.scroll$ = this.windowEventsService.scrollListener$.pipe(debounceTime(25), takeUntil(this.destroy$));

    this.headerBG$ = this.scroll$.pipe(
      map(() => {
        if (window.scrollY < this.gallerySection?.nativeElement.offsetHeight) {
          return HeaderBG.TRANSPARENTBG;
        } else if (
          window.scrollY > this.gallerySection?.nativeElement.offsetHeight &&
          this.editorTipsSection?.nativeElement.getBoundingClientRect().bottom < 0
        ) {
          return HeaderBG.WHITEBG;
        } else {
          return HeaderBG.INSIDERSBG;
        }
      }),
    );

    this.scrollRatio$ = this.scroll$.pipe(
      map(() => {
        this.maxScroll = this.maxScroll ? this.maxScroll : this.calculateMaxScroll();
        return this.maxScroll ? 100 * (window.scrollY / this.maxScroll) : 0;
      }),
    );
    this.resize$ = this.windowEventsService.resizeListener$;
    this.isMobileResolution$ = this.windowEventsService.isMobileResolution$;
  }

  calculateMaxScroll(): number {
    return document.documentElement.scrollHeight - document.documentElement.clientHeight;
  }

  onBackEvent(): void {
    const prevUrl = this.historyService.getPreviousUrl();
    if (prevUrl) {
      this.location.back();
    } else {
      this.router.navigate(['/trip', this.hash]);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
  }

  onTitlePosition(titleYOffset: number) {
    this.titleVisible$ = this.scroll$.pipe(
      map(() => {
        return window.scrollY > titleYOffset;
      }),
    );
  }
}
